import React from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';

const CancelSubscriptionDialog = ({
    open,
    onClose,
    cancelMode,
    setCancelMode,
    periodEndDate,
    handleConfirmCancel,
}) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '24px',
                    padding: 1,
                    boxShadow: '0 24px 48px rgba(0,0,0,0.1)'
                }
            }}
        >
            <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #f3f4f6', pt: 3, px: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827', letterSpacing: '-0.5px' }}>
                    {cancelMode === 'immediate' ? 'Cancel Immediately?' : 'Cancel Subscription'}
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 4, px: 4 }}>
                <Box sx={{ mb: 4, pt: 1 }}>
                    <Typography variant="body1" sx={{ color: '#4b5563', fontSize: '1.0rem', lineHeight: 1.6 }}>
                        {cancelMode === 'immediate'
                            ? "You will lose access to all premium features instantly. This action cannot be undone and your configurations may be disabled."
                            : "Your subscription will remain active until the end of your scheduled billing period. You will not be charged again."
                        }
                    </Typography>
                </Box>

                <Box sx={{
                    backgroundColor: '#f9fafb',
                    p: 3,
                    borderRadius: '16px',
                    border: '1px solid #e5e7eb'
                }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#374151', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.75rem' }}>
                        Cancellation Options
                    </Typography>
                    <FormControl component="fieldset" sx={{ width: '100%' }}>
                        <RadioGroup value={cancelMode} onChange={(e) => setCancelMode(e.target.value)}>
                            <Box sx={{
                                mb: 2,
                                p: 2,
                                borderRadius: '12px',
                                backgroundColor: cancelMode === 'period_end' ? '#eff6ff' : '#ffffff',
                                border: cancelMode === 'period_end' ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                                transition: 'all 0.2s',
                                cursor: 'pointer'
                            }} onClick={() => setCancelMode('period_end')}>
                                <FormControlLabel
                                    value="period_end"
                                    control={<Radio sx={{ color: '#3b82f6', '&.Mui-checked': { color: '#3b82f6' }, p: 0, mr: 1.5 }} />}
                                    label={
                                        <Box>
                                            <Typography variant="body1" sx={{ fontWeight: cancelMode === 'period_end' ? 700 : 500, color: '#1f2937' }}>
                                                Cancel at end of period <Chip label="Recommended" size="small" sx={{ ml: 1, height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#dbeafe', color: '#1e40af' }} />
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                                                Keep premium access until {periodEndDate}
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
                                />
                            </Box>

                            <Box sx={{
                                p: 2,
                                borderRadius: '12px',
                                backgroundColor: cancelMode === 'immediate' ? '#fef2f2' : '#ffffff',
                                border: cancelMode === 'immediate' ? '2px solid #ef4444' : '1px solid #e5e7eb',
                                transition: 'all 0.2s',
                                cursor: 'pointer'
                            }} onClick={() => setCancelMode('immediate')}>
                                <FormControlLabel
                                    value="immediate"
                                    control={<Radio sx={{ color: '#ef4444', '&.Mui-checked': { color: '#ef4444' }, p: 0, mr: 1.5 }} />}
                                    label={
                                        <Box>
                                            <Typography variant="body1" sx={{ fontWeight: cancelMode === 'immediate' ? 700 : 500, color: cancelMode === 'immediate' ? '#b91c1c' : '#1f2937' }}>
                                                Cancel immediately
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: cancelMode === 'immediate' ? '#ef4444' : '#6b7280', mt: 0.5 }}>
                                                Lose access right now without a refund
                                            </Typography>
                                        </Box>
                                    }
                                    sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
                                />
                            </Box>
                        </RadioGroup>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 4, pb: 4, pt: 2, gap: 2, justifyContent: 'space-between' }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        flex: 1,
                        color: '#4b5563',
                        borderColor: '#e5e7eb',
                        fontWeight: 700,
                        px: 3,
                        py: 1.5,
                        borderRadius: '14px',
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        '&:hover': {
                            borderColor: '#9ca3af',
                            backgroundColor: '#f9fafb',
                            transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s'
                    }}
                >
                    Keep It
                </Button>
                <Button
                    disableElevation
                    onClick={handleConfirmCancel}
                    variant="contained"
                    sx={{
                        flex: 1,
                        backgroundColor: cancelMode === 'immediate' ? '#ef4444' : '#1f2937',
                        color: '#fff',
                        fontWeight: 700,
                        px: 3,
                        py: 1.5,
                        borderRadius: '14px',
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        boxShadow: cancelMode === 'immediate' ? '0 4px 14px rgba(239, 68, 68, 0.25)' : '0 4px 14px rgba(31, 41, 55, 0.2)',
                        '&:hover': {
                            backgroundColor: cancelMode === 'immediate' ? '#dc2626' : '#111827',
                            transform: 'translateY(-1px)',
                            boxShadow: cancelMode === 'immediate' ? '0 8px 20px rgba(239, 68, 68, 0.35)' : '0 8px 20px rgba(31, 41, 55, 0.3)',
                        },
                        transition: 'all 0.2s'
                    }}
                >
                    {cancelMode === 'immediate' ? 'Cancel Now' : 'Confirm Cancellation'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CancelSubscriptionDialog;
